import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ThumbsUp, Undo2 } from "lucide-react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import type { Listing } from "@/types";
import { Heart, Star, Sticker } from "@/components/Doodles";
import { Checklist } from "@/components/Checklist";
import { EtsyPanel } from "@/components/EtsyPanel";

export default function ReviewPage() {
  const meta = useMeta();
  const [tab, setTab] = useState<"in_review" | "approved">("in_review");
  const [items, setItems] = useState<Listing[] | null>(null);
  const [idx, setIdx] = useState(0);
  const [note, setNote] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [showDesc, setShowDesc] = useState(false);

  const load = useCallback(() => {
    api.get("/listings", { params: { status: tab } }).then((r) => { setItems(r.data); setIdx(0); });
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const l = items?.[idx];
  const replace = (next: Listing) => setItems((xs) => xs!.map((x) => (x.id === next.id ? next : x)));
  const dropCurrent = () => { setItems((xs) => xs!.filter((x) => x.id !== l!.id)); setIdx((i) => Math.max(0, Math.min(i, (items?.length || 1) - 2))); };

  const toggle = async (key: string, value: boolean) => {
    replace({ ...l!, checks: l!.checks.map((c) => (c.key === key ? { ...c, ok: value } : c)) });
    try {
      const { data } = await api.patch(`/listings/${l!.id}`, { checklist: { [key]: value } });
      replace(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const act = async (action: "approve" | "reject") => {
    try {
      await api.post(`/listings/${l!.id}/review`, { action, note });
      toast.success(action === "approve" ? "Approved and added to the publish queue." : "Sent back with your note");
      setNote(""); setRejecting(false); dropCurrent();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const allOk = l?.checks.every((c) => c.ok);

  return (
    <div className="mx-auto max-w-md" data-testid="review-page">
      <div className="mb-5 flex items-center justify-between">
        <h1 className="font-display text-3xl font-black text-[#23324A]">Review</h1>
        <div className="flex rounded-full border-2 border-[#23324A] bg-white p-1">
          {(["in_review", "approved"] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`review-tab-${t}`}
              className={`rounded-full px-3 py-1 text-xs font-bold ${tab === t ? "bg-[#23324A] text-white" : "text-[#23324A]"}`}>{t === "in_review" ? "To review" : "Approved"}</button>
          ))}
        </div>
      </div>

      {items && !l && (
        <div className="paper-card relative p-8 text-center" data-testid="review-empty">
          <Star className="mx-auto mb-3 h-12 w-12" />
          <p className="font-display text-xl font-extrabold text-[#23324A]">{tab === "in_review" ? "All caught up!" : "Nothing approved yet"}</p>
          <p className="mt-2 text-sm text-[#23324A]/70">{tab === "in_review" ? "New listings land here when they finish building." : "Approve listings to send them to Etsy."}</p>
          <Link to="/admin/listings" className="btn-outline mt-5 inline-flex" data-testid="review-empty-back">Back to listings</Link>
        </div>
      )}

      {l && (
        <article className="pb-32" data-testid="review-card">
          <div className="mb-3 flex items-center justify-between text-xs font-bold text-[#23324A]/70">
            <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} data-testid="review-prev-btn" className="flex items-center disabled:opacity-30"><ChevronLeft className="h-4 w-4" strokeWidth={3} />Prev</button>
            <span data-testid="review-position">{idx + 1} of {items!.length}</span>
            <button onClick={() => setIdx((i) => Math.min(items!.length - 1, i + 1))} disabled={idx >= items!.length - 1} data-testid="review-next-btn" className="flex items-center disabled:opacity-30">Next<ChevronRight className="h-4 w-4" strokeWidth={3} /></button>
          </div>
          <div className="relative overflow-hidden rounded-[22px] border-2 border-[#23324A] shadow-[5px_5px_0_#23324A]">
            {l.thumbnail_path && <img src={fileUrl(l.thumbnail_web_path || l.thumbnail_path)} alt="Thumbnail" className="aspect-[5/4] w-full object-cover" data-testid="review-thumbnail" />}
            <Heart className="absolute -left-3 -top-3 h-10 w-10 -rotate-12" />
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {l.categories.map((c, i) => <Sticker key={c} label={c} tilt={i % 2 ? 3 : -3} />)}
            <span className="ml-auto font-display text-2xl font-black text-[#F08A6C]" data-testid="review-price">${l.price.toFixed(2)}</span>
          </div>
          <h2 className="mt-3 font-display text-lg font-extrabold leading-snug text-[#23324A]" data-testid="review-title">{l.title || "(no title yet)"}</h2>
          <p className="mt-1 text-xs text-[#23324A]/60">{l.title.length}/140 characters</p>
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="review-tags">
            {l.tags.map((t) => <span key={t} className="rounded-full bg-[#9CC9E8]/40 px-2.5 py-1 text-xs font-semibold text-[#23324A]">{t}</span>)}
          </div>
          <button onClick={() => setShowDesc((s) => !s)} className="mt-4 text-sm font-bold text-[#23324A] underline decoration-[#F08A6C] decoration-2 underline-offset-4" data-testid="review-toggle-description">
            {showDesc ? "Hide description" : "Read full description"}
          </button>
          {showDesc && <p className="mt-3 whitespace-pre-line rounded-2xl bg-white p-4 text-sm leading-relaxed text-[#23324A]" data-testid="review-description">{l.etsy_description}</p>}
          <div className="-mx-1 mt-5 flex gap-3 overflow-x-auto px-1 pb-2" data-testid="review-previews">
            {l.preview_paths.map((p, i) => <img key={p} src={fileUrl(p)} alt={`Page ${i + 1}`} className="h-44 shrink-0 rounded-lg border-2 border-[#23324A] bg-white" loading="lazy" />)}
          </div>
          <div className="mt-6"><Checklist checks={l.checks} onToggle={toggle} disabled={tab !== "in_review"} /></div>
          <Link to={`/admin/listings/${l.id}`} className="mt-5 inline-block text-sm font-bold text-[#23324A] underline underline-offset-4" data-testid="review-edit-link">Edit this listing</Link>
          {tab === "approved" && <div className="mt-5"><EtsyPanel listing={l} demo={!!meta?.etsy_demo} onChange={(n) => { replace(n); toast.message("Moved out of Approved"); }} /></div>}
        </article>
      )}

      {l && tab === "in_review" && (
        <div className="fixed inset-x-0 bottom-[68px] z-30 border-t-2 border-[#23324A] bg-[#FDFBF7] px-4 py-3 md:bottom-0 md:left-64" data-testid="review-action-bar">
          <div className="mx-auto max-w-md">
            {rejecting && (
              <textarea className="field mb-2 min-h-[64px] text-sm" placeholder="What needs fixing?" value={note} onChange={(e) => setNote(e.target.value)} data-testid="reject-note-input" />
            )}
            <div className="flex gap-3">
              <button className="btn-outline flex-1 justify-center" onClick={() => (rejecting ? act("reject") : setRejecting(true))} data-testid="review-reject-btn">
                <Undo2 className="h-4 w-4" strokeWidth={2.5} /> {rejecting ? "Send back" : "Needs work"}
              </button>
              <button className="btn-coral flex-1 justify-center" disabled={!allOk} onClick={() => act("approve")} data-testid="review-approve-btn">
                <ThumbsUp className="h-4 w-4" strokeWidth={2.5} /> Approve
              </button>
            </div>
            {!allOk && <p className="mt-1.5 text-center text-[11px] font-semibold text-[#23324A]/60" data-testid="review-approve-hint">Tick every check to approve</p>}
          </div>
        </div>
      )}
    </div>
  );
}
